import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBrain, 
  faEye, 
  faSearch, 
  faClock, 
  faEnvelope,
  faTasks,
  faCheckCircle,
  faSpinner,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';

interface AIActivity {
  type: 'scanning' | 'analyzing' | 'monitoring' | 'updating' | 'idle' | 'processing';
  description: string;
  icon: any;
  color: string;
  pulse: boolean;
}

interface AIStatusIndicatorProps {
  dealId: string;
  hasAICoordinator?: boolean;
  currentStage?: string;
  lastActivity?: Date;
}

const AIStatusIndicator: React.FC<AIStatusIndicatorProps> = ({
  dealId,
  hasAICoordinator,
  currentStage,
  lastActivity
}) => {
  const [currentActivity, setCurrentActivity] = useState<AIActivity>({
    type: 'monitoring',
    description: 'MONITORING DEAL',
    icon: faEye,
    color: '#667eea',
    pulse: false
  });
  const [isProcessingContract, setIsProcessingContract] = useState(false);

  const activities: AIActivity[] = [
    {
      type: 'scanning',
      description: 'SCANNING EMAILS',
      icon: faEnvelope,
      color: '#3b82f6',
      pulse: true
    },
    {
      type: 'analyzing',
      description: 'ANALYZING DOCUMENTS',
      icon: faSearch,
      color: '#10b981',
      pulse: true
    },
    {
      type: 'monitoring',
      description: 'CHECKING DEADLINES',
      icon: faClock,
      color: '#f59e0b',
      pulse: true
    },
    {
      type: 'updating',
      description: 'UPDATING TASKS',
      icon: faTasks,
      color: '#8b5cf6',
      pulse: true
    },
    {
      type: 'idle',
      description: 'AI MONITORING',
      icon: faBrain,
      color: '#667eea',
      pulse: false
    }
  ];

  // Check for contract processing status
  useEffect(() => {
    if (!dealId) return;
    
    const checkContractProcessing = async () => {
      try {
        const response = await axiosInstance.get(`/deals/${dealId}`);
        if (response.data?.data?.deal?.DealAttachments) {
          const attachments = response.data.data.deal.DealAttachments;
          const processingContract = attachments.some((att: any) => 
            (att.processing_status === 'pending' || att.processing_status === 'processing') &&
            att.attachment_title?.toLowerCase().match(/contract|agreement|purchase/)
          );
          setIsProcessingContract(processingContract);
        }
      } catch (error) {
        console.error('Failed to check contract processing:', error);
      }
    };
    
    // Check immediately
    checkContractProcessing();
    
    // Check every 3 seconds
    const interval = setInterval(checkContractProcessing, 3000);
    
    return () => clearInterval(interval);
  }, [dealId]);

  // Simulate AI activity rotation
  useEffect(() => {
    // If processing contract, show that instead of random activities
    if (isProcessingContract) {
      setCurrentActivity({
        type: 'analyzing',
        description: 'ANALYZING CONTRACT',
        icon: faFileContract,
        color: '#10b981',
        pulse: true
      });
      return;
    }
    
    // More frequent updates if AI Coordinator is active
    const interval = hasAICoordinator ? 5000 : 10000;
    
    const timer = setInterval(() => {
      // Pick a random activity (weighted towards idle)
      const random = Math.random();
      let nextActivity;
      
      if (random < 0.7) {
        // 70% chance of idle state
        nextActivity = activities.find(a => a.type === 'idle')!;
      } else {
        // 30% chance of active state
        const activeActivities = activities.filter(a => a.type !== 'idle');
        nextActivity = activeActivities[Math.floor(Math.random() * activeActivities.length)];
      }
      
      setCurrentActivity(nextActivity);
      
      // Auto-return to idle after 3 seconds if in active state
      if (nextActivity.type !== 'idle') {
        setTimeout(() => {
          setCurrentActivity(activities.find(a => a.type === 'idle')!);
        }, 3000);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [hasAICoordinator, isProcessingContract]);

  // Special handling for AI Coordinator
  useEffect(() => {
    if (hasAICoordinator && currentActivity.type === 'idle') {
      setCurrentActivity({
        type: 'processing',
        description: 'AI COORDINATOR ACTIVE',
        icon: faBrain,
        color: '#10b981',
        pulse: true
      });
    }
  }, [hasAICoordinator, currentActivity.type]);

  return (
    <div 
      className="d-flex align-items-center"
      style={{
        background: `linear-gradient(135deg, ${currentActivity.color}15 0%, ${currentActivity.color}10 100%)`,
        padding: '8px 16px',
        borderRadius: '24px',
        border: `1px solid ${currentActivity.color}30`,
        transition: 'all 0.5s ease',
        cursor: 'pointer',
        userSelect: 'none'
      }}
      title={`Last activity: ${lastActivity ? new Date(lastActivity).toLocaleTimeString() : 'Just now'}`}
    >
      <div 
        className="d-flex align-items-center justify-content-center me-2"
        style={{
          width: '20px',
          height: '20px',
          position: 'relative'
        }}
      >
        {currentActivity.pulse && (
          <div 
            className="pulse-ring"
            style={{
              position: 'absolute',
              width: '20px',
              height: '20px',
              border: `2px solid ${currentActivity.color}`,
              borderRadius: '50%',
              animation: 'pulse-ring 1.5s ease-out infinite',
              opacity: 0.6
            }}
          />
        )}
        <FontAwesomeIcon 
          icon={currentActivity.icon}
          style={{ 
            color: currentActivity.color,
            fontSize: '14px',
            animation: currentActivity.pulse ? 'pulse-icon 1.5s ease-in-out infinite' : 'none'
          }}
        />
      </div>
      <span 
        className="fw-semibold"
        style={{ 
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: currentActivity.color
        }}
      >
        {currentActivity.description}
      </span>
      
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes pulse-ring {
            0% {
              transform: scale(0.8);
              opacity: 1;
            }
            100% {
              transform: scale(1.5);
              opacity: 0;
            }
          }
          
          @keyframes pulse-icon {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }
        `
      }} />
    </div>
  );
};

export default AIStatusIndicator;